/**
 * Audio Engine for Learn Mode
 * 
 * Unified audio system with:
 * - Consistent volume normalization
 * - Phoneme playback
 * - Feedback sounds (correct/incorrect)
 * - No dependency on ElevenLabs during teaching loop
 */

// Singleton AudioContext
let audioContext: AudioContext | null = null;
let masterGain: GainNode | null = null;

// Audio cache
const audioBufferCache = new Map<string, AudioBuffer>();

// Active audio source tracking — used by stopAllAudio() to interrupt playback
let activeSource: AudioBufferSourceNode | null = null;
let activeGainNode: GainNode | null = null;

// =============================================================================
// Volume Configuration
// =============================================================================

// Target peak level for normalization (0.0 to 1.0)
// ElevenLabs outputs around 0.7-0.8 peak, so we target similar
const TARGET_PEAK = 0.9;

// Max normalization multiplier (prevent distortion on very quiet files)
const MAX_NORMALIZATION = 5.0;  // Up from 3.0 - some files are VERY quiet

// Default boost for proprietary phoneme files
let phonemeBoost = 2.5;  // 2.5x boost by default

// Per-phoneme boost overrides (vowels tend to be recorded quieter)
const PHONEME_BOOST_OVERRIDES: Record<string, number> = {
  // Vowels (colors) - need extra boost
  black: 3.5,
  red: 3.5,
  green: 3.0,
  blue: 3.0,
  pink: 3.5,
  yellow: 3.0,
  orange: 3.0,
  brown: 3.0,
  plum: 3.5,
  aqua: 3.0,
  lime: 3.0,
  wood: 3.0,
};

// Volume levels for generated sounds
const VOLUME = {
  FEEDBACK_CORRECT: 0.6,
  FEEDBACK_RETRY: 0.5,
  FEEDBACK_CELEBRATE: 0.7,
};

/**
 * Get boost level for a specific phoneme
 */
function getPhonemeBoostLevel(phoneme: string): number {
  return PHONEME_BOOST_OVERRIDES[phoneme.toLowerCase()] || phonemeBoost;
}

/**
 * Adjust phoneme volume boost (1.0 = normal, 2.0 = double, etc.)
 * Use this if proprietary files are too quiet/loud relative to TTS
 */
export function setPhonemeBoost(boost: number): void {
  phonemeBoost = Math.max(0.5, Math.min(boost, 5.0)); // Clamp between 0.5x and 5x
  console.log('[AudioEngine] Phoneme boost set to:', phonemeBoost);
}

/**
 * Get current phoneme boost level
 */
export function getPhonemeBoost(): number {
  return phonemeBoost;
}

// =============================================================================
// TTS Instructions (via Edge Function → ElevenLabs API)
// =============================================================================

import { supabase } from '@/lib/supabase/client';

// Addison voice ID
const ELEVENLABS_VOICE_ID = 'zlTdYp3pkavOrG8wiS0a';
const ELEVENLABS_MODEL = 'eleven_turbo_v2';

// TTS audio cache
const ttsCache = new Map<string, AudioBuffer>();

// Pending TTS requests (deduplication)
const pendingTTS = new Map<string, Promise<AudioBuffer | null>>();

// Rate limiting
let lastTTSRequest = 0;
const TTS_MIN_INTERVAL_MS = 200; // Minimum 200ms between requests

/**
 * Generate speech using ElevenLabs TTS via edge function (server-side API key)
 * Returns audio buffer that can be played through our audio engine
 */
async function generateTTS(text: string): Promise<AudioBuffer | null> {
  const cacheKey = text.toLowerCase().trim();

  // Check cache first
  if (ttsCache.has(cacheKey)) {
    return ttsCache.get(cacheKey)!;
  }

  // Check if already pending (deduplication)
  if (pendingTTS.has(cacheKey)) {
    return pendingTTS.get(cacheKey)!;
  }

  // Create promise for this request
  const requestPromise = (async (): Promise<AudioBuffer | null> => {
    try {
      // Rate limiting: wait if needed
      const now = Date.now();
      const timeSinceLastRequest = now - lastTTSRequest;
      if (timeSinceLastRequest < TTS_MIN_INTERVAL_MS) {
        await new Promise(r => setTimeout(r, TTS_MIN_INTERVAL_MS - timeSinceLastRequest));
      }
      lastTTSRequest = Date.now();

      console.log('[AudioEngine] Generating TTS:', text);

      const { data, error } = await supabase.functions.invoke('text-to-speech-elevenlabs', {
        body: {
          text,
          voice: ELEVENLABS_VOICE_ID,
          model_id: ELEVENLABS_MODEL,
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.75,
            style: 0.3,
            use_speaker_boost: true,
          },
        },
      });

      if (error) {
        console.error('[AudioEngine] TTS edge function error:', error);
        return null;
      }

      if (!data?.audioContent) {
        console.error('[AudioEngine] TTS: no audio content returned');
        return null;
      }

      // Decode base64 audio to ArrayBuffer
      const binaryString = atob(data.audioContent);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const ctx = getContext();
      const audioBuffer = await ctx.decodeAudioData(bytes.buffer);

      // Cache it
      ttsCache.set(cacheKey, audioBuffer);
      console.log('[AudioEngine] TTS cached:', text);

      return audioBuffer;
    } catch (err) {
      console.error('[AudioEngine] TTS generation failed:', err);
      return null;
    } finally {
      // Clean up pending map
      pendingTTS.delete(cacheKey);
    }
  })();

  // Store in pending map
  pendingTTS.set(cacheKey, requestPromise);

  return requestPromise;
}

/**
 * Speak text using ElevenLabs TTS
 * Falls back to browser TTS if ElevenLabs fails
 */
export async function speak(text: string): Promise<void> {
  try {
    const buffer = await generateTTS(text);
    
    if (buffer) {
      await playBuffer(buffer, 1.0);
      return;
    }
  } catch (err) {
    console.warn('[AudioEngine] ElevenLabs TTS failed, using browser fallback:', err);
  }
  
  // Fallback to browser TTS
  await browserSpeak(text);
}

/**
 * Browser TTS fallback
 */
function browserSpeak(text: string): Promise<void> {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) {
      resolve();
      return;
    }
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.1;
    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
    window.speechSynthesis.speak(utterance);
  });
}

// Track if preload has been initiated
let ttsPreloadStarted = false;

/**
 * Pre-generate common TTS phrases for faster playback
 * Sequential to avoid rate limiting, runs only once
 */
export async function preloadTTSPhrases(): Promise<void> {
  // Only run once (React Strict Mode protection)
  if (ttsPreloadStarted) return;
  ttsPreloadStarted = true;
  
  const phrases = [
    'Listen.',
    'Great job!',
    'Almost. Try again!',
  ];
  
  // Generate sequentially with delays to avoid rate limiting
  for (const phrase of phrases) {
    // Skip if already cached
    if (ttsCache.has(phrase.toLowerCase().trim())) continue;
    
    await generateTTS(phrase);
    // Small delay between requests
    await new Promise(r => setTimeout(r, 100));
  }
  
  console.log('[AudioEngine] TTS phrases preloaded');
}

// Sound descriptions for prompts
const SOUND_NAMES: Record<string, string> = {
  // Colors (vowels)
  black: 'ah',
  red: 'eh',
  green: 'ee',
  blue: 'oo',
  pink: 'ih',
  yellow: 'oh',
  orange: 'or',
  brown: 'ow',
  plum: 'uh',
  aqua: 'aw',
  lime: 'eye',
  wood: 'oo',
  // Consonants
  b: 'buh', c: 'kuh', d: 'duh', g: 'guh', h: 'huh',
  j: 'juh', k: 'kuh', l: 'luh', m: 'mmm', n: 'nnn',
  r: 'rrr', s: 'sss', t: 'tuh', y: 'yuh', z: 'zzz',
  ck: 'kuh',
};

/**
 * Get the spoken name for a phoneme
 */
export function getPhonemeSpokenName(phoneme: string): string {
  return SOUND_NAMES[phoneme.toLowerCase()] || phoneme;
}

/**
 * Initialize audio engine
 * 
 * IMPORTANT: Call this on a user interaction (click/tap) to unlock audio playback.
 * Browser autoplay policy requires user gesture before AudioContext can play.
 */
export function initAudioEngine(): AudioContext {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new AudioContext({ sampleRate: 44100 });
    masterGain = audioContext.createGain();
    masterGain.gain.value = 1.0;
    masterGain.connect(audioContext.destination);
    console.log('[AudioEngine] Created new AudioContext');
  }
  
  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    console.log('[AudioEngine] AudioContext suspended, resuming...');
    audioContext.resume().then(() => {
      console.log('[AudioEngine] AudioContext resumed successfully');
    }).catch(err => {
      console.error('[AudioEngine] Failed to resume AudioContext:', err);
    });
  }
  
  return audioContext;
}

/**
 * Initialize audio engine and wait for it to be ready
 * Use this before playing audio to ensure the context is not suspended
 */
export async function ensureAudioReady(): Promise<AudioContext> {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new AudioContext({ sampleRate: 44100 });
    masterGain = audioContext.createGain();
    masterGain.gain.value = 1.0;
    masterGain.connect(audioContext.destination);
    console.log('[AudioEngine] Created new AudioContext');
  }
  
  // Resume if suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    console.log('[AudioEngine] AudioContext suspended, awaiting resume...');
    await audioContext.resume();
    console.log('[AudioEngine] AudioContext resumed, state:', audioContext.state);
  }
  
  return audioContext;
}

/**
 * Get or create AudioContext
 */
function getContext(): AudioContext {
  if (!audioContext) {
    return initAudioEngine();
  }
  return audioContext;
}

/**
 * Get master gain node
 */
function getMasterGain(): GainNode {
  if (!masterGain) {
    initAudioEngine();
  }
  return masterGain!;
}

/**
 * Load audio file into buffer (cached)
 */
async function loadAudioBuffer(url: string): Promise<AudioBuffer> {
  // Check cache
  if (audioBufferCache.has(url)) {
    return audioBufferCache.get(url)!;
  }
  
  const ctx = getContext();
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to load audio: ${url}`);
  }
  
  const arrayBuffer = await response.arrayBuffer();
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
  
  // Cache it
  audioBufferCache.set(url, audioBuffer);
  
  return audioBuffer;
}

/**
 * Analyze audio buffer and return peak amplitude
 */
function getPeakAmplitude(buffer: AudioBuffer): number {
  let peak = 0;
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const data = buffer.getChannelData(channel);
    for (let i = 0; i < data.length; i++) {
      const abs = Math.abs(data[i]);
      if (abs > peak) peak = abs;
    }
  }
  return peak;
}

/**
 * Calculate gain needed to normalize audio to target peak
 */
function getNormalizationGain(buffer: AudioBuffer, targetPeak: number = TARGET_PEAK): number {
  const currentPeak = getPeakAmplitude(buffer);
  if (currentPeak === 0) return 1.0;
  
  // Allow higher normalization for very quiet files
  const gain = Math.min(targetPeak / currentPeak, MAX_NORMALIZATION);
  return gain;
}

/**
 * Play audio buffer with automatic normalization and optional duration trim.
 *
 * @param maxDurationMs - If provided, trims playback to this duration (ms).
 *   Uses Web Audio `stop(when)` scheduling so the source node ends cleanly
 *   without decoding a new buffer. Useful for consonant .m4a files that have
 *   trailing silence or natural decay that feels disconnected from TTS.
 */
function playBuffer(
  buffer: AudioBuffer,
  volume: number = 1.0,
  normalize: boolean = false,
  maxDurationMs?: number
): Promise<void> {
  return new Promise((resolve) => {
    const ctx = getContext();
    const source = ctx.createBufferSource();
    const gainNode = ctx.createGain();

    source.buffer = buffer;

    // Calculate final gain with two ceilings:
    // 1. Anti-clip: output peak = peakAmplitude × gain ≤ 0.95
    // 2. Anti-noise: total gain ≤ 8.0 (prevents amplifying recording noise
    //    floor on very quiet files, e.g. peak=0.05 would otherwise get 12.5×)
    const MAX_TOTAL_GAIN = 8.0;
    let finalGain = volume;
    if (normalize) {
      const normGain = getNormalizationGain(buffer);
      const desiredGain = volume * normGain;
      const peak = getPeakAmplitude(buffer);
      const antiClipGain = peak > 0 ? (0.95 / peak) : 1.0;
      finalGain = Math.min(desiredGain, antiClipGain, MAX_TOTAL_GAIN);
      console.log('[AudioEngine] Normalize: peak=', peak.toFixed(3), 'norm=', normGain.toFixed(2), '× boost=', volume.toFixed(1), '→ final=', finalGain.toFixed(2), '(out≈', (peak * finalGain).toFixed(2), ')');
    }

    gainNode.gain.value = finalGain;

    source.connect(gainNode);
    gainNode.connect(getMasterGain());

    // Track active source so stopAllAudio() can interrupt it
    activeSource = source;
    activeGainNode = gainNode;

    source.onended = () => {
      if (activeSource === source) {
        activeSource = null;
        activeGainNode = null;
      }
      resolve();
    };

    // If trimming, schedule stop after maxDurationMs with a short fade-out
    if (maxDurationMs && maxDurationMs > 0) {
      const trimSec = maxDurationMs / 1000;
      const actualDuration = Math.min(trimSec, buffer.duration);
      const fadeMs = 30; // 30ms fade-out to avoid click
      const fadeStart = Math.max(0, actualDuration - fadeMs / 1000);
      gainNode.gain.setValueAtTime(finalGain, ctx.currentTime + fadeStart);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + actualDuration);
      source.start(0);
      source.stop(ctx.currentTime + actualDuration);
    } else {
      source.start(0);
    }
  });
}

// =============================================================================
// Phoneme Audio Playback
// =============================================================================

// Phonemes with proprietary audio files
const PHONEME_FILES: Record<string, string> = {
  // Colors (vowel sounds)
  black: '/audio/Isolated Phoneme_ Black.m4a',
  red: '/audio/Isolated Phoneme_ Red.m4a',
  green: '/audio/Isolated Phoneme_ Green.m4a',
  blue: '/audio/Isolated Phoneme_ Blue.m4a',
  pink: '/audio/Isolated Phoneme_ Pink.m4a',
  yellow: '/audio/Isolated Phoneme_ Yellow.m4a',
  orange: '/audio/Isolated Phoneme_ Orange.m4a',
  brown: '/audio/Isolated Phoneme_ Brown.m4a',
  plum: '/audio/Isolated Phoneme_ Plum.m4a',
  aqua: '/audio/Isolated Phoneme_ Aqua.m4a',
  lime: '/audio/Isolated Phoneme_ Lime.m4a',
  wood: '/audio/Isolated Phoneme_ Wood.m4a',
  // All consonants (complete set)
  b: '/audio/Isolated Phoneme_ B.m4a',
  c: '/audio/Isolated Phoneme_ C.m4a',
  d: '/audio/Isolated Phoneme_ D.m4a',
  f: '/audio/Isolated Phoneme_ F.m4a',
  g: '/audio/Isolated Phoneme_ G.m4a',
  h: '/audio/Isolated Phoneme_ H.m4a',
  j: '/audio/Isolated Phoneme_ J.m4a',
  k: '/audio/Isolated Phoneme_ K.m4a',
  l: '/audio/Isolated Phoneme_ L.m4a',
  m: '/audio/Isolated Phoneme_ M.m4a',
  n: '/audio/Isolated Phoneme_ N.m4a',
  p: '/audio/Isolated Phoneme_ P.m4a',
  r: '/audio/Isolated Phoneme_ R.m4a',
  s: '/audio/Isolated Phoneme_ S.m4a',
  t: '/audio/Isolated Phoneme_ T.m4a',
  v: '/audio/Isolated Phoneme_ V.m4a',
  w: '/audio/Isolated Phoneme_ W.m4a',
  x: '/audio/Isolated Phoneme_ X.m4a',
  y: '/audio/Isolated Phoneme_ Y.m4a',
  z: '/audio/Isolated Phoneme_ Z.m4a',
  ck: '/audio/Isolated Phoneme_ CK.m4a',
};

// TTS pronunciation for missing phonemes (short, isolated sounds)
// TTS fallback only for phonemes without audio files
const TTS_PHONEME_TEXT: Record<string, string> = {
  q: 'kwuh',  // Only Q is missing
};

/**
 * Play a phoneme sound with normalization + boost
 * 
 * Proprietary phoneme files are normalized to match ElevenLabs TTS volume,
 * then boosted to ensure clarity for children.
 * Vowels (colors) get extra boost as they're often recorded quieter.
 */
export async function playPhoneme(phoneme: string, maxDurationMs?: number): Promise<void> {
  const key = phoneme.toLowerCase();
  const url = PHONEME_FILES[key];

  // If we have a proprietary audio file, use it
  if (url) {
    try {
      const buffer = await loadAudioBuffer(url);
      const boost = getPhonemeBoostLevel(key);
      await playBuffer(buffer, boost, true, maxDurationMs);
      console.log('[AudioEngine] ✓ Played phoneme:', phoneme, `(boost: ${boost}x${maxDurationMs ? `, trim: ${maxDurationMs}ms` : ''})`);
      return;
    } catch (err) {
      console.error('[AudioEngine] Failed to play phoneme file:', phoneme, err);
      // Fall through to TTS
    }
  }

  // Use TTS for missing phonemes
  const ttsText = TTS_PHONEME_TEXT[key];
  if (ttsText) {
    console.log('[AudioEngine] Using TTS for phoneme:', phoneme, `("${ttsText}")`);
    const buffer = await generateTTS(ttsText);
    if (buffer) {
      await playBuffer(buffer, 1.0, true, maxDurationMs);
      console.log('[AudioEngine] ✓ Played TTS phoneme:', phoneme);
      return;
    }
  }

  console.warn('[AudioEngine] Unknown phoneme and no TTS fallback:', phoneme);
}

/**
 * Preload phonemes for faster playback (both proprietary and TTS)
 */
export async function preloadPhonemes(phonemes: string[]): Promise<void> {
  for (const p of phonemes) {
    const key = p.toLowerCase();
    const url = PHONEME_FILES[key];
    
    if (url) {
      // Preload proprietary audio
      try {
        await loadAudioBuffer(url);
        console.log('[AudioEngine] Preloaded:', p);
      } catch {
        // Ignore preload failures
      }
    } else {
      // Preload TTS for missing phonemes
      const ttsText = TTS_PHONEME_TEXT[key];
      if (ttsText) {
        try {
          await generateTTS(ttsText);
          console.log('[AudioEngine] Preloaded TTS:', p, `("${ttsText}")`);
        } catch {
          // Ignore preload failures
        }
      }
    }
  }
}

// =============================================================================
// Feedback Sounds (Generated with Web Audio API)
// =============================================================================

/**
 * Play "correct" feedback sound - cheerful ascending tone
 */
export async function playCorrectSound(): Promise<void> {
  const ctx = getContext();
  const now = ctx.currentTime;
  
  // Two-note ascending "ding-ding!"
  const frequencies = [523.25, 659.25]; // C5, E5
  
  frequencies.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = freq;
    
    gain.gain.setValueAtTime(0, now + i * 0.15);
    gain.gain.linearRampToValueAtTime(VOLUME.FEEDBACK_CORRECT, now + i * 0.15 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.3);
    
    osc.connect(gain);
    gain.connect(getMasterGain());
    
    osc.start(now + i * 0.15);
    osc.stop(now + i * 0.15 + 0.35);
  });
  
  // Wait for sound to complete
  await new Promise(resolve => setTimeout(resolve, 500));
}

/**
 * Play "try again" feedback sound - gentle neutral tone
 */
export async function playRetrySound(): Promise<void> {
  const ctx = getContext();
  const now = ctx.currentTime;
  
  // Soft single tone
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  
  osc.type = 'sine';
  osc.frequency.value = 392; // G4
  
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(VOLUME.FEEDBACK_RETRY, now + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
  
  osc.connect(gain);
  gain.connect(getMasterGain());
  
  osc.start(now);
  osc.stop(now + 0.5);
  
  await new Promise(resolve => setTimeout(resolve, 500));
}

/**
 * Play celebration sound - triumphant chord
 */
export async function playCelebrateSound(): Promise<void> {
  const ctx = getContext();
  const now = ctx.currentTime;
  
  // Major chord: C-E-G
  const frequencies = [523.25, 659.25, 783.99];
  
  frequencies.forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = freq;
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(VOLUME.FEEDBACK_CELEBRATE / 3, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 1.0);
    
    osc.connect(gain);
    gain.connect(getMasterGain());
    
    osc.start(now);
    osc.stop(now + 1.1);
  });
  
  await new Promise(resolve => setTimeout(resolve, 1000));
}

/**
 * Play "listen" prompt sound - attention getter
 */
export async function playListenSound(): Promise<void> {
  const ctx = getContext();
  const now = ctx.currentTime;
  
  // Quick ascending notes
  const frequencies = [440, 554.37, 659.25]; // A4, C#5, E5
  
  frequencies.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'triangle';
    osc.frequency.value = freq;
    
    const startTime = now + i * 0.08;
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.15);
    
    osc.connect(gain);
    gain.connect(getMasterGain());
    
    osc.start(startTime);
    osc.stop(startTime + 0.2);
  });
  
  await new Promise(resolve => setTimeout(resolve, 350));
}

// =============================================================================
// Full Teaching Sequence
// =============================================================================

export interface TeachingAudioResult {
  played: boolean;
  phoneme: string;
}

/**
 * Play the full teaching sequence for a step:
 * 
 * ARCHITECTURE:
 * - ElevenLabs TTS: Instructions ("Listen", "Now you say it")
 * - Proprietary Audio: Actual phoneme sound from /audio/ folder
 * 
 * Sequence:
 * 1. [TTS] "Listen to this sound"
 * 2. [PROPRIETARY] Phoneme audio file
 * 3. [TTS] "Now you say it"
 * 4. [SILENCE] Wait for audio to fully stop before mic opens
 */
export async function playTeachingSequence(phoneme: string): Promise<TeachingAudioResult> {
  console.log('[AudioEngine] Starting teaching sequence for:', phoneme);
  
  try {
    // Ensure audio context is ready
    const ctx = getContext();
    if (ctx.state === 'suspended') {
      console.log('[AudioEngine] Resuming suspended audio context');
      await ctx.resume();
    }
    
    // 1. [TTS] Short introduction
    console.log('[AudioEngine] TTS: Introduction');
    await speak('Listen.');
    
    // 2. Brief pause
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 3. [PROPRIETARY] Play phoneme
    console.log('[AudioEngine] PROPRIETARY: Playing phoneme:', phoneme);
    await playPhoneme(phoneme);
    
    // 4. Minimal wait - mic opens immediately after phoneme
    console.log('[AudioEngine] Ready for mic');
    await new Promise(resolve => setTimeout(resolve, 100));
    
    console.log('[AudioEngine] Teaching sequence complete - ready for mic');
    return { played: true, phoneme };
  } catch (err) {
    console.error('[AudioEngine] Teaching sequence failed:', err);
    return { played: false, phoneme };
  }
}

// =============================================================================
// Audio Sequence Player (Consonant Practice)
// =============================================================================

/**
 * A segment in an audio sequence — TTS speech, phoneme file, or silence.
 * Used by consonant practice sub-steps to compose split-phrase audio
 * where TTS handles instructional text and playPhoneme() handles the
 * actual consonant sound from proprietary .m4a files.
 */
export type AudioSegment =
  | { type: 'tts'; text: string }
  | { type: 'phoneme'; phoneme: string; maxDurationMs?: number }
  | { type: 'pause'; ms: number };

/**
 * Immediately stop all audio playback. Fades out over 50ms to avoid click.
 * Call this before starting a new flow to prevent overlapping audio.
 */
export function stopAllAudio(): void {
  // Stop active buffer source
  if (activeSource && activeGainNode) {
    try {
      const ctx = getContext();
      activeGainNode.gain.setValueAtTime(activeGainNode.gain.value, ctx.currentTime);
      activeGainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
      activeSource.stop(ctx.currentTime + 0.05);
    } catch {
      // source may already be stopped
    }
    activeSource = null;
    activeGainNode = null;
  }

  // Cancel browser TTS if running
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
}

/**
 * Play a sequence of audio segments in order.
 *
 * Ensures AudioContext is ready, then plays each segment sequentially:
 * - 'tts': ElevenLabs Addison voice via speak()
 * - 'phoneme': Proprietary .m4a file via playPhoneme()
 * - 'pause': Silent gap
 *
 * @param abortSignal - Optional. Checked between each segment. If the
 *   signal returns true, playback stops immediately. Used by the consonant
 *   practice flow to cleanly interrupt when a new card is scanned.
 */
export async function playAudioSequence(
  segments: AudioSegment[],
  abortSignal?: () => boolean
): Promise<void> {
  try {
    await ensureAudioReady();

    for (const seg of segments) {
      if (abortSignal?.()) {
        console.log('[AudioEngine] Sequence aborted before segment:', seg.type);
        return;
      }

      switch (seg.type) {
        case 'tts':
          await speak(seg.text);
          break;
        case 'phoneme':
          await playPhoneme(seg.phoneme, seg.maxDurationMs);
          break;
        case 'pause':
          await new Promise(r => setTimeout(r, seg.ms));
          break;
      }
    }
  } catch (err) {
    console.error('[AudioEngine] Audio sequence failed:', err);
  }
}

/** @deprecated Use playAudioSequence() with AudioSegment[] instead */
export async function playConsonantReview(phoneme: string, phrase: string): Promise<void> {
  await playAudioSequence([
    { type: 'tts', text: phrase },
    { type: 'pause', ms: 200 },
    { type: 'phoneme', phoneme },
  ]);
}

/**
 * Speak feedback using ElevenLabs TTS (no beeps - TTS is enough)
 */
export async function speakFeedback(isCorrect: boolean): Promise<void> {
  if (isCorrect) {
    await speak('Great job!');
  } else {
    await speak('Almost. Try again!');
  }
}

/**
 * Play feedback based on result
 */
export async function playFeedback(isCorrect: boolean): Promise<void> {
  if (isCorrect) {
    await playCorrectSound();
  } else {
    await playRetrySound();
  }
}
